
import { computed, defineComponent, onMounted, reactive, toRefs } from "vue";
import {
  fetchNodes,
  fetchSites,
  fetchManufacturingCompanies,
  fetchTurbineModel,
  fetchBladeManufacturersForGivenTurbine,
  fetchBladeModelsForGivenTurbine,
  fetchCountries,
  fetchSystemAggregators,
  fetchCustomers,
  fetchCustomersNames,
  patchNode,
  postNewTurbine,
} from "@/helpers/api";
import {
  Sites,
  TurbineModel,
  Companies,
  Customers,
  SystemAggregators,
  BladeModels,
  Node,
  NewTurbineForm,
} from "@/interfaces/systemsdb";

export default defineComponent({
  name: "NewTurbineModal",
  props: {
    selectedItem: {
      type: Object,
      required: true,
    },
  },
  emits: ["close", "save"],
  setup(props, { emit }) {
    const state = reactive({
      loading: true as boolean,

      // Site config
      countries: [] as string[],
      sites: [] as Sites[],
      customerNames: [] as string[],
      customerSubgroups: [] as Customers[],

      // Turbine config
      companies: [] as Companies[],
      turbineModels: [] as TurbineModel[],
      aggregators: [] as SystemAggregators[],

      // Blade config
      bladeManufacturers: [] as string[],
      bladeAModels: [] as BladeModels[],
      bladeBModels: [] as BladeModels[],
      bladeCModels: [] as BladeModels[],

      sampleRate: 62.5 as number,
      nodes: [] as Node[],
      selectedNode11: null as string | null,
      selectedNode12: null as string | null,
      selectedNode21: null as string | null,
      selectedNode22: null as string | null,
      selectedNode31: null as string | null,
      selectedNode32: null as string | null,
      saving: false as boolean,

      form: {
        country: null as string | null,
        site_id: null as number | null,
        customer_name: null as string | null,
        customer_id: null as number | null, //formed off of customer and customer subgroup
        turbine_name: "",
        turbine_designer_id: null as number | null, //from companies.company_id
        turbine_model: null as string | null,
        aggregator_serial: null as string | null, //from aggregators.aggregator_serial
        blade_A_sn: "",
        blade_B_sn: "",
        blade_C_sn: "",
        blade_A_manufacturer: null as string | null, //from companies.company_id
        blade_B_manufacturer: null as string | null,
        blade_C_manufacturer: null as string | null,
        blade_A_model: null as string | null, //from blade_models.blade_model_id
        blade_B_model: null as string | null,
        blade_C_model: null as string | null,
        include_drivetrain: true as boolean,
        include_tower: true as boolean,
      },
      errorMsg: "" as string,
    });

    const loadSitesForCountry = async () => {
      state.form.site_id = null;
      state.sites = [];

      if (!state.form.country) {
        return;
      }

      try {
        state.sites = await fetchSites(state.form.country, undefined, 10000);
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to load sites";
      }
    };

    const loadCustomerSubgroupsForCustomer = async () => {
      state.form.customer_id = null;
      state.customerSubgroups = [];

      if (!state.form.customer_name) {
        return;
      }

      try {
        state.customerSubgroups = await fetchCustomers(state.form.customer_name);
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to load customers";
      }
    };

    const resetBladeSelections = () => {
      state.form.blade_A_manufacturer = null;
      state.form.blade_B_manufacturer = null;
      state.form.blade_C_manufacturer = null;
      state.form.blade_A_model = null;
      state.form.blade_B_model = null;
      state.form.blade_C_model = null;

      state.bladeManufacturers = [];
      state.bladeAModels = [];
      state.bladeBModels = [];
      state.bladeCModels = [];
    };

    const loadTurbineModelsForDesigner = async () => {
      state.form.turbine_model = null;
      state.turbineModels = [];
      resetBladeSelections();

      if (!state.form.turbine_designer_id) {
        return;
      }

      try {
        state.turbineModels = await fetchTurbineModel(String(state.form.turbine_designer_id));
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to load turbine models";
      }
    };

    const loadBladeManufacturersForTurbineModel = async () => {
      resetBladeSelections();

      if (!state.form.turbine_model) {
        return;
      }

      try {
        state.bladeManufacturers = await fetchBladeManufacturersForGivenTurbine(state.form.turbine_model);
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to load blade manufacturers";
      }
    };

    const loadBladeModelsForBlade = async (blade: "A" | "B" | "C") => {
      let manufacturer: string | null = null;

      if (blade === "A") {
        manufacturer = state.form.blade_A_manufacturer;
        state.form.blade_A_model = null;
        state.bladeAModels = [];
      } else if (blade === "B") {
        manufacturer = state.form.blade_B_manufacturer;
        state.form.blade_B_model = null;
        state.bladeBModels = [];
      } else {
        manufacturer = state.form.blade_C_manufacturer;
        state.form.blade_C_model = null;
        state.bladeCModels = [];
      }

      if (!state.form.turbine_model || !manufacturer) {
        return;
      }

      try {
        const bladeModels = await fetchBladeModelsForGivenTurbine(state.form.turbine_model, manufacturer);

        if (blade === "A") {
          state.bladeAModels = bladeModels;
        } else if (blade === "B") {
          state.bladeBModels = bladeModels;
        } else {
          state.bladeCModels = bladeModels;
        }
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to load blade models";
      }
    };

    const nodesForAddress = (address: number) => state.nodes.filter((n) => n.address === address && !n.aggregator_name);

    const isFw18 = (idHex1: string | null, idHex2: string | null): boolean =>
      [idHex1, idHex2].some((id) => id && state.nodes.find((n) => n.id_hex === id)?.firmware === "1.8");

    const siteName = computed(() => state.sites.find((s) => s.site_id === state.form.site_id)?.name ?? "");

    const tomlPreview = computed(() => {
      const site = siteName.value;
      const turbine = state.form.turbine_name || "";
      const asset = state.form.turbine_name || "";
      const sr = state.sampleRate;
      const snA = state.form.blade_A_sn || "";
      const snB = state.form.blade_B_sn || "";
      const snC = state.form.blade_C_sn || "";

      const nodebus = (usb: string, fw18: boolean) =>
        fw18 ? `"serial:///dev/${usb}?baudrate=256000"` : `"serial:///dev/${usb}"`;

      const block = (usb: string, nodeBase: number, blade: string, sn: string, fw18: boolean) =>
        `[[Streaming]]\nName = "${site} ${turbine}"\nNodebus = ${nodebus(
          usb,
          fw18
        )}\nGroupAddress = 200\nSampleRate = ${sr}\nChunkDuration ="10m"\nTimeAlignTolerance = "10ms"\nSensors = [\n    {Name = "${blade}1", Node = ${
          nodeBase * 10 + 1
        }, Sensor = "node.adxl.acceleration/2.5g", Tags = {Units = "g", BladeName = "S/N: ${sn}", Location = "1", Blade = "${blade}", Asset = "${asset}"}},\n    {Name = "${blade}2", Node = ${
          nodeBase * 10 + 2
        }, Sensor = "node.adxl.acceleration/10g", Tags = {Units = "g", BladeName = "S/N: ${sn}", Location = "2", Blade = "${blade}", Asset = "${asset}"}},\n]`;

      return [
        block("ttyUSB1", 1, "A", snA, isFw18(state.selectedNode11, state.selectedNode12)),
        block("ttyUSB2", 2, "B", snB, isFw18(state.selectedNode21, state.selectedNode22)),
        block("ttyUSB3", 3, "C", snC, isFw18(state.selectedNode31, state.selectedNode32)),
      ].join("\n\n");
    });

    const loadOptions = async () => {
      state.loading = true;
      try {
        const [companiesData, customerData, countriesData, aggregatorsData, nodesData] = await Promise.all([
          fetchManufacturingCompanies(),
          fetchCustomersNames(),
          fetchCountries(),
          fetchSystemAggregators(),
          fetchNodes(),
        ]);

        state.sites = [];
        state.nodes = nodesData;
        state.customerNames = customerData;
        state.customerSubgroups = [];
        state.companies = companiesData;
        state.turbineModels = [];
        resetBladeSelections();
        state.countries = countriesData;
        state.aggregators = aggregatorsData as SystemAggregators[];
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to load options";
      } finally {
        state.loading = false;
      }
    };

    const save = async () => {
      state.errorMsg = "";
      state.saving = true;

      try {
        const saveResult = await postNewTurbine({ ...state.form });

        // Resolve aggregator name from the selected serial for node assignment
        const aggName = state.aggregators.find((a) => String(a.serial) === state.form.aggregator_serial)?.name ?? null;

        const bladeIds: Record<string, number> = saveResult.blade_ids ?? {};

        const nodeAssignments: [string | null, string][] = [
          [state.selectedNode11, "A"],
          [state.selectedNode12, "A"],
          [state.selectedNode21, "B"],
          [state.selectedNode22, "B"],
          [state.selectedNode31, "C"],
          [state.selectedNode32, "C"],
        ];

        await Promise.all(
          nodeAssignments
            .filter(([idHex]) => idHex)
            .map(([idHex, blade]) => patchNode(idHex!, aggName, bladeIds[blade] ? String(bladeIds[blade]) : blade))
        );

        try {
          const bytes = new TextEncoder().encode(tomlPreview.value);
          const binary = Array.from(bytes)
            .map((b) => String.fromCharCode(b))
            .join("");
          await navigator.clipboard.writeText(btoa(binary));
        } catch {
          // Clipboard write is best-effort; don't fail the save if it's unavailable
        }

        emit("save", {
          form: {
            ...state.form,
          },
        });

        emit("close");
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to save turbine";
      } finally {
        state.saving = false;
      }
    };

    const close = () => {
      emit("close");
    };

    onMounted(loadOptions);

    return {
      ...toRefs(state),
      siteName,
      tomlPreview,
      nodesForAddress,
      save,
      close,
      loadSitesForCountry,
      loadCustomerSubgroupsForCustomer,
      loadTurbineModelsForDesigner,
      loadBladeManufacturersForTurbineModel,
      loadBladeModelsForBlade,
      postNewTurbine,
    };
  },
});
